import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Bot, Cloud, GitBranch, MessageSquareCode, Terminal, Wrench,
} from "lucide-react";
import api from "@/lib/api";
import { usePublicConfig } from "@/lib/useConfig";
import { PublicFooter, PublicNav } from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/Primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const FAQ_ITEMS = [
  ["Do I need cloud experience to start?", "No. The assessment measures where you are across ten categories and the roadmap starts you at the right level — including skipping modules you already know."],
  ["How long does the track take?", "Most developers finish in 10–16 weeks at 6–10 hours per week. Your roadmap is paced against the weekly hours you commit during onboarding."],
  ["Is this just videos?", "No videos. Short written lessons with production-grade commands and configs, hands-on exercises, AI review of your work, troubleshooting labs, a capstone and mock interviews."],
  ["What does the AI actually do?", "It reviews your Terraform, Dockerfiles, GitHub Actions YAML, IAM policies and logs; it mentors you in six modes; and it runs mock interviews with follow-up questions and a scored evaluation."],
  ["Do I need an AWS account?", "Not for the lessons or labs — troubleshooting scenarios are simulated. For the capstone you can use an AWS free-tier account if you want to deploy for real."],
  ["Can I cancel anytime?", "Yes. Plans are monthly and you keep access to your readiness report and portfolio artifacts."],
];

const FEATURES = [
  { icon: Terminal, title: "Skill assessment", body: "29 questions across Linux, networking, Git, Docker, AWS, Terraform, CI/CD, monitoring, security and troubleshooting. Scored 0–100 per category." },
  { icon: GitBranch, title: "Personalized roadmap", body: "12 modules re-ordered, shortened or reinforced based on your scores, target role and weekly hours." },
  { icon: Bot, title: "AI Mentor, six modes", body: "Teach me, explain simply, give me a hint, review my answer, show a real example, ask me an interview question." },
  { icon: Cloud, title: "AI code & config review", body: "Structured review of Terraform, Dockerfiles, Actions workflows, Bash, IAM policies, security groups and logs." },
  { icon: Wrench, title: "Troubleshooting labs", body: "Production-style incidents scored on your reasoning, not on guessing the answer." },
  { icon: MessageSquareCode, title: "Mock interviews", body: "AI-led interviews with follow-ups and a final evaluation you can actually act on." },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const { trialDays } = usePublicConfig();
  useEffect(() => {
    api.get("/subscriptions/plans").then(({ data }) => setPlans(data.plans)).catch(() => setPlans([]));
  }, []);

  return (
    <div className="min-h-screen">
      <PublicNav />

      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-7">
            <p className="overline text-primary" data-testid="hero-overline">for developers with 2+ years shipping code</p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter leading-none">
              Stop reading about DevOps.<br />
              <span className="text-primary">Prove you can do it.</span>
            </h1>
            <p className="mt-6 text-base sm:text-sm text-muted-foreground max-w-xl leading-relaxed">
              A measured path from application developer to DevOps, Cloud, Platform or SRE engineer.
              Assessment first, then a roadmap built for your gaps — with AI review on every artifact
              you write and a readiness report you can take into interviews.
            </p>
            <p className="mt-4 font-mono text-xs text-primary" data-testid="hero-trial-note">
              {trialDays} days of full access, free. No card required.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button size="lg" className="rounded-none gap-2">
                  Start free assessment <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-cta-secondary">
                <Button size="lg" variant="outline" className="rounded-none">See pricing</Button>
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md border-t border-border pt-6">
              {[["12", "modules"], ["36", "lessons"], ["10", "skill categories"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl font-bold tracking-tighter">{n}</p>
                  <p className="overline text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 md:pt-10">
            <CodeBlock
              testId="hero-code"
              label="readiness.sh"
              code={`$ devops-mentor readiness --user you\n\nLinux & Shell      ████████░░  82  ready\nNetworking         █████░░░░░  54  developing\nDocker             ███████░░░  71  developing\nTerraform          ███░░░░░░░  31  needs work\nCI/CD              ████░░░░░░  44  needs work\n\n→ roadmap: 11 modules, 13 weeks @ 8h/wk\n→ next: Terraform state & backends`}
            />
            <p className="mt-4 font-mono text-xs text-muted-foreground">
              Your real report looks like this, generated from your own answers.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <p className="overline text-muted-foreground">what you get</p>
        <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight leading-none max-w-2xl">
          Everything a hiring manager asks you to prove.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
          {FEATURES.map(({ icon: Icon, title, body }, i) => (
            <div
              key={title}
              data-testid={`feature-${i}`}
              className="border-r border-b border-border p-7 hover:bg-accent/40 transition-colors duration-150"
            >
              <Icon className="h-5 w-5 text-primary" />
              <h3 className="mt-5 font-display text-lg font-bold tracking-tight">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-accent/30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="overline text-muted-foreground">the loop</p>
            <h2 className="mt-4 text-2xl sm:text-3xl font-display font-bold tracking-tight leading-none">
              Measure, learn, prove, repeat.
            </h2>
          </div>
          <ol className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
            {[
              ["01", "Assess", "29 questions, ten categories, scored per skill with strengths and gaps."],
              ["02", "Roadmap", "Modules skipped, shortened or reinforced and paced to your calendar."],
              ["03", "Practice", "Write real Terraform, Dockerfiles and pipelines; get structured AI review."],
              ["04", "Prove", "Capstone deployment, mock interviews and a job-readiness report."],
            ].map(([n, t, b]) => (
              <li key={n}>
                <span className="font-mono text-xs text-primary">{n}</span>
                <h3 className="mt-2 font-display text-lg font-bold tracking-tight">{t}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline text-muted-foreground">pricing</p>
            <h2 className="mt-4 text-2xl sm:text-3xl font-display font-bold tracking-tight">Start free. Upgrade when it earns it.</h2>
          </div>
          <Link to="/pricing" className="text-sm text-primary hover:underline" data-testid="pricing-teaser-link">Full comparison →</Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {plans.map((p) => (
            <div key={p.slug} data-testid={`plan-teaser-${p.slug}`} className={`bg-background p-7 ${p.highlighted ? "ring-1 ring-inset ring-primary" : ""}`}>
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-lg font-bold tracking-tight">{p.name}</h3>
                {p.highlighted && <span className="overline text-primary">popular</span>}
              </div>
              <p className="mt-4 font-display text-3xl font-bold tracking-tighter">
                ${p.priceMonthly}
                <span className="text-sm font-normal text-muted-foreground">/mo</span>
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{p.tagline}</p>
              <ul className="mt-5 space-y-2 text-sm">
                {p.features.slice(0, 4).map((f) => (
                  <li key={f} className="flex gap-2 text-muted-foreground">
                    <span className="text-primary font-mono">›</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-20">
        <p className="overline text-muted-foreground">faq</p>
        <h2 className="mt-4 text-2xl sm:text-3xl font-display font-bold tracking-tight">Questions engineers actually ask.</h2>
        <Accordion type="single" collapsible className="mt-8">
          {FAQ_ITEMS.slice(0, 4).map(([q, a], i) => (
            <AccordionItem key={q} value={`i${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-sm font-medium">{q}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">Find your gaps in 15 minutes.</h2>
            <p className="mt-2 text-sm text-muted-foreground">Free assessment plus {trialDays} days of full access. No card. Your roadmap is generated immediately after.</p>
          </div>
          <Link to="/register" data-testid="footer-cta">
            <Button size="lg" className="rounded-none gap-2">Start free assessment <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
